import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShoppingCart, Moon } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useProducts } from "@/hooks/useProducts";
import { useBundles } from "@/hooks/useBundles";
import { useSiteContent } from "@/hooks/useSiteContent";
import productImage from "@/assets/product-package.jpeg";
import { products as fallbackProducts } from "@/data/products";

const PLACEHOLDER_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'%3E%3Crect fill='%23f0f0f0' width='400' height='400'/%3E%3Ctext fill='%23999' font-family='sans-serif' font-size='24' x='50%25' y='50%25' text-anchor='middle' dominant-baseline='middle'%3EGeen afbeelding%3C/text%3E%3C/svg%3E";

export const Blends = () => {
  const [hoveredBlend, setHoveredBlend] = useState<string | null>(null);
  const { addItem } = useCart();
  const { products: blends, isLoading } = useProducts();
  const { bundles, isLoading: bundlesLoading } = useBundles();
  const { content } = useSiteContent('blends');


  // Filter tea blends (not accessories) - already sorted by display_order from database
  // Merge fallback + backend by slug; keep fallback images when backend is missing/placeholder
  const isPlaceholder = (url?: string) =>
    !url || url.includes('placeholder') || url.includes('Geen afbeelding') || url.startsWith('data:image/svg+xml');

  const mergedBlends = fallbackProducts.map(fp => {
    const db = blends.find(p => p.slug === fp.slug);
    if (!db) return fp;
    return {
      ...fp,
      name: db.name || fp.name,
      slug: fp.slug,
      tagline: db.tagline || fp.tagline,
      description: db.description || fp.description,
      longDescription: db.longDescription || fp.longDescription,
      price25g: db.price25g || fp.price25g,
      price50g: db.price50g || fp.price50g,
      bundlePrice: db.bundlePrice || fp.bundlePrice,
      originalPrice: db.originalPrice || fp.originalPrice,
      accentColor: (db as any).accentColor || fp.accentColor,
      borderColor: (db as any).borderColor || fp.borderColor,
      glowColor: (db as any).glowColor || fp.glowColor,
      icon: (db as any).icon || fp.icon,
      bullets: db.bullets?.length ? db.bullets : fp.bullets,
      ingredients: db.ingredients?.length ? db.ingredients : fp.ingredients,
      benefits: db.benefits?.length ? db.benefits : fp.benefits,
      productImage: isPlaceholder(db.productImage) ? fp.productImage : db.productImage,
      lifestyleImage: isPlaceholder(db.lifestyleImage) ? fp.lifestyleImage : db.lifestyleImage,
      detailImage: isPlaceholder(db.detailImage) ? fp.detailImage : db.detailImage,
      testimonials: db.testimonials?.length ? db.testimonials : fp.testimonials,
      isPopular: db.isPopular ?? fp.isPopular,
      isBestDeal: db.isBestDeal ?? fp.isBestDeal,
      isAccessory: db.isAccessory ?? fp.isAccessory,
      usage: db.usage || fp.usage,
      contentInfo: db.contentInfo || fp.contentInfo,
      seoDescription: db.seoDescription || fp.seoDescription,
      id: (db as any).id || fp.id,
    };
  });

  const teaBlends = mergedBlends.filter(b => !b.isAccessory).slice(0, 3);
  console.debug('[Blends] counts', { blends: blends.length, merged: mergedBlends.length, display: teaBlends.length });
  
  // Diagnostische log voor blends zonder foto's
  if (teaBlends.length > 0) {
    const blendsWithoutImages = teaBlends.filter(b => 
      !b.productImage || b.productImage.includes('placeholder') || b.productImage.includes('Geen afbeelding')
    );
    if (blendsWithoutImages.length > 0) {
      console.warn('[Blends] Blends zonder foto\'s:', blendsWithoutImages.map(b => ({ slug: b.slug, image: b.productImage?.substring(0, 50) })));
    }
  }

  return (
    <section id="blends" className="relative py-24 section-light overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 animate-on-scroll">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-gold mb-6 text-glow">
            {content?.title || 'Ontdek Onze Thee Blends'}
          </h2>
          
          <div className="w-20 h-1 bg-gradient-gold mx-auto mb-8 rounded-full shadow-gold" />
          
          <p className="text-lg text-foreground max-w-2xl mx-auto leading-relaxed">
            {content?.description || 'Elke blend is zorgvuldig samengesteld om balans en wellness te bevorderen'}
          </p>
        </div>

        {/* Blends Grid - Mobile Optimized */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-16">
          {teaBlends.map((blend, index) => {
            const IconComponent: any = (blend as any).icon || Moon;
            return (
              <div
                key={blend.slug}
                className={`group relative bg-card rounded-2xl ${blend.borderColor} border-2 p-6 sm:p-8 transition-transform duration-300 origin-center transform-gpu @media(hover:hover):hover:scale-105 hover:shadow-xl ${
                  hoveredBlend === blend.slug ? blend.glowColor : 'shadow-lg'
                }`}
                style={{ animationDelay: `${index * 0.2}s` }}
                onMouseEnter={() => setHoveredBlend(blend.slug)}
                onMouseLeave={() => setHoveredBlend(null)}
              >
                {/* Accent Background */}
                <div className={`absolute top-0 right-0 w-20 sm:w-24 h-20 sm:h-24 ${blend.accentColor} rounded-2xl opacity-20 transform rotate-12 group-hover:rotate-6 transition-transform duration-500 pointer-events-none`} />
                
                {/* Icon */}
                <div className={`w-14 sm:w-16 h-14 sm:h-16 ${blend.accentColor} rounded-full flex items-center justify-center mb-4 sm:mb-6 relative z-10`}>
                  <IconComponent className="w-7 sm:w-8 h-7 sm:h-8 text-primary" />
                </div>

                {/* Product Image with Lifestyle Background */}
                <div className="relative mb-4 sm:mb-6 animate-on-scroll overflow-hidden rounded-xl">
                  {/* Tea Ceremony Background */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-all duration-700">
                    <img
                      src={blend.lifestyleImage}
                      alt={`${blend.name} tea ceremony`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'%3E%3Crect fill='%23f0f0f0' width='400' height='400'/%3E%3Ctext fill='%23999' font-family='sans-serif' font-size='24' x='50%25' y='50%25' text-anchor='middle' dominant-baseline='middle'%3EGeen afbeelding%3C/text%3E%3C/svg%3E";
                      }}
                    />
                  </div>
                  
                  {/* Main Product Image */}
                  <img
                    src={blend.productImage}
                    alt={blend.name}
                    className="relative z-10 w-full h-40 sm:h-48 object-cover rounded-xl shadow-md group-hover:shadow-xl transition-all duration-300 group-hover:scale-105"
                    onError={(e) => {
                      e.currentTarget.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'%3E%3Crect fill='%23f0f0f0' width='400' height='400'/%3E%3Ctext fill='%23999' font-family='sans-serif' font-size='24' x='50%25' y='50%25' text-anchor='middle' dominant-baseline='middle'%3EGeen afbeelding%3C/text%3E%3C/svg%3E";
                    }}
                  />
                  <div className={`absolute inset-0 ${blend.accentColor} rounded-xl opacity-10 group-hover:opacity-20 transition-opacity duration-300 pointer-events-none`} />
                </div>

                {/* Content */}
                <div className="relative z-10 animate-on-scroll">
                  <h3 className="font-serif text-xl sm:text-2xl lg:text-3xl font-black text-foreground mb-2 sm:mb-3 leading-tight tracking-tight">
                    {blend.name}
                  </h3>
                  
                  <p className="text-base sm:text-lg font-light italic text-gold/80 mb-4 sm:mb-6 tracking-wide leading-relaxed">
                    {blend.tagline}
                  </p>

                  {/* Bullets */}
                  <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                    {blend.bullets.map((bullet, idx) => (
                      <li key={idx} className="flex items-start gap-2 sm:gap-3 text-sm sm:text-base font-medium text-foreground leading-relaxed">
                        <span className="w-1.5 sm:w-2 h-1.5 sm:h-2 bg-gold rounded-full mt-2 sm:mt-2.5 flex-shrink-0 shadow-gold" />
                        {bullet}
                      </li>
                    ))}
                  </ul>

                  {/* Price & CTA - Mobile Optimized */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
                    <span className="text-2xl sm:text-3xl font-black text-gold tracking-tighter text-glow">{blend.price25g}</span>
                    <Button
                      onClick={() => {
                        addItem({
                          id: `${blend.name.toLowerCase().replace(/['&\s]/g, '-')}-25g`,
                          name: blend.name,
                          size: '25g',
                          price: 11.11
                        });
                      }}
                      className="btn-hero animate-zen-glow font-semibold w-full sm:w-auto min-h-[44px] touch-manipulation group"
                    >
                      <span className="flex items-center justify-center gap-2">
                        <ShoppingCart className="w-4 h-4 group-hover:animate-bounce" />
                        Ontdek
                      </span>
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Dynamic Bundle Section */}
        {!bundlesLoading && bundles.length > 0 && bundles[0].products && bundles[0].products.length > 0 ? (
          <div className="bg-gradient-to-r from-card to-muted/50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 text-center animate-on-scroll border border-gold/20">
            <div className="max-w-2xl mx-auto">
              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-gold mb-3 sm:mb-4 text-glow">
                {bundles[0].name}
              </h3>
              
              <p className="text-base sm:text-lg text-foreground mb-6 sm:mb-8 leading-relaxed">
                {bundles[0].description}
              </p>

              {/* Bundle Images - Dynamic */}
              <div className="flex justify-center items-center gap-2 sm:gap-4 mb-6 sm:mb-8 overflow-x-auto">
                {bundles[0].products.map((product, index) => (
                  <div key={`${product.name}-${index}`} className="relative flex-shrink-0">
                    <img
                      src={product.productImage}
                      alt={product.name}
                      className="w-12 sm:w-16 h-16 sm:h-20 object-cover rounded-lg shadow-md"
                      onError={(e) => e.currentTarget.src = PLACEHOLDER_IMAGE}
                    />
                    {index < bundles[0].products!.length - 1 && (
                      <span className="absolute -right-2 sm:-right-3 top-1/2 -translate-y-1/2 text-lg sm:text-2xl font-bold text-primary">
                        +
                      </span>
                    )}
                  </div>
                ))}
                <div className="text-gold font-bold text-lg sm:text-xl mx-2 sm:mx-4 flex-shrink-0">=</div>
                <Badge className="bg-destructive text-white px-3 sm:px-4 py-1 sm:py-2 text-base sm:text-lg flex-shrink-0">
                  -{bundles[0].discountPercentage}%
                </Badge>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mb-8">
                <div className="text-center">
                  <span className="text-sm text-muted-foreground line-through">€{bundles[0].originalPrice?.toFixed(2)}</span>
                  <span className="text-2xl sm:text-3xl font-bold text-gold ml-2">€{bundles[0].bundlePrice?.toFixed(2)}</span>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
                <Button
                  onClick={() => {
                    bundles[0].products?.forEach(product => {
                      addItem({
                        id: `${product.id}-25g`,
                        name: product.name,
                        size: '25g',
                        price: parseFloat(product.price25g.replace('€', ''))
                      });
                    });
                  }}
                  className="btn-hero text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 animate-zen-glow w-full sm:w-auto min-h-[44px] touch-manipulation group"
                >
                  <span className="flex items-center justify-center gap-3">
                    <ShoppingCart className="w-5 h-5 group-hover:animate-bounce" />
                    Ontdek Volledige Set
                  </span>
                </Button>
              </div>
            </div>
          </div>
        ) : (
          // Fallback - Static Bundle Display
          <div className="bg-gradient-to-r from-card to-muted/50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 text-center animate-on-scroll border border-gold/20">
            <div className="max-w-2xl mx-auto">
              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-gold mb-3 sm:mb-4 text-glow">
                Creëer jouw ritueel
              </h3>
              
              <p className="text-base sm:text-lg text-foreground mb-6 sm:mb-8 leading-relaxed">
                Combineer alle 3 blends en ontdek welke het beste bij jouw balans past. Een luxe collectie voor elk moment van de dag.
              </p>

              {/* Bundle Images - Static Fallback */}
              <div className="flex justify-center items-center gap-2 sm:gap-4 mb-6 sm:mb-8 overflow-x-auto">
                {teaBlends.slice(0, 3).map((blend, index) => (
                  <div key={index} className="relative flex-shrink-0">
                    <img
                      src={blend.productImage}
                      alt={blend.name}
                      className="w-12 sm:w-16 h-16 sm:h-20 object-cover rounded-lg shadow-md"
                      onError={(e) => e.currentTarget.src = PLACEHOLDER_IMAGE}
                    />
                    {index < 2 && (
                      <span className="absolute -right-2 sm:-right-3 top-1/2 -translate-y-1/2 text-lg sm:text-2xl font-bold text-primary">
                        +
                      </span>
                    )}
                  </div>
                ))}
                <div className="text-gold font-bold text-lg sm:text-xl mx-2 sm:mx-4 flex-shrink-0">=</div>
                <Badge className="bg-destructive text-white px-3 sm:px-4 py-1 sm:py-2 text-base sm:text-lg flex-shrink-0">
                  -15%
                </Badge>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mb-8">
                <div className="text-center">
                  <span className="text-sm text-muted-foreground line-through">€33,33</span>
                  <span className="text-2xl sm:text-3xl font-bold text-gold ml-2">€29,95</span>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
                <Button
                  onClick={() => {
                    addItem({
                      id: 'complete-bundle',
                      name: 'Complete Ritual Bundle',
                      size: '3x 25g',
                      price: 29.95
                    });
                  }}
                  className="btn-hero text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 animate-zen-glow w-full sm:w-auto min-h-[44px] touch-manipulation group"
                >
                  <span className="flex items-center justify-center gap-3">
                    <ShoppingCart className="w-5 h-5 group-hover:animate-bounce" />
                    Ontdek Volledige Set
                  </span>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};